import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';

interface CorrectiveMaintenanceDetail {
  id: number;
  veiculo: string;
  descricao: string;
  data_solicitacao: string;
  data_inicio: string;
  data_conclusao: string;
  custo_estimado: number;
  custo_real: number;
  fornecedor: string;
  status: string;
  prioridade: string;
  tipo_problema: string;
  sintomas: string;
  causa_raiz: string;
  solucao_aplicada: string;
  tempo_total: number;
  observacoes: string;
  criado_em: string;
  atualizado_em: string;
  veiculo_info?: {
    placa: string;
    modelo: string;
    marca: string;
    ano: number;
  };
  fornecedor_info?: {
    nome: string;
    cnpj: string;
    telefone: string;
    endereco: string;
  };
}

const CorrectiveMaintenanceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState<CorrectiveMaintenanceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMaintenance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMaintenance = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID não fornecido');
        return;
      }

      const response = await apiService.getCorrectiveMaintenance(parseInt(id));

      if (response.success && response.data) {
        const apiData = response.data;

        const mappedMaintenance: CorrectiveMaintenanceDetail = {
          id: apiData.id,
          veiculo: apiData.veiculo || apiData.veiculo_placa || 'N/A',
          descricao: apiData.descricao || '',
          data_solicitacao: apiData.data_solicitacao,
          data_inicio: apiData.data_inicio || '',
          data_conclusao: apiData.data_conclusao || '',
          custo_estimado: apiData.custo_estimado || 0,
          custo_real: apiData.custo_real || 0,
          fornecedor: apiData.fornecedor || apiData.fornecedor_nome || 'N/A',
          status: apiData.status || 'Solicitada',
          prioridade: apiData.prioridade || 'Média',
          tipo_problema: apiData.tipo_problema || '',
          sintomas: apiData.sintomas || '',
          causa_raiz: apiData.causa_raiz || '',
          solucao_aplicada: apiData.solucao_aplicada || '',
          tempo_total: apiData.tempo_total || 0,
          observacoes: apiData.observacoes || '',
          criado_em: apiData.criado_em || apiData.created_at,
          atualizado_em: apiData.atualizado_em || apiData.updated_at,
          veiculo_info: apiData.veiculo_info,
          fornecedor_info: apiData.fornecedor_info
        };

        setMaintenance(mappedMaintenance);
      } else {
        setError('Manutenção não encontrada');
        utils.showNotification('Manutenção corretiva não encontrada', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao carregar manutenção corretiva:', error);
      setError('Erro ao carregar detalhes da manutenção corretiva');
      utils.showNotification(error.message || 'Erro ao carregar detalhes da manutenção corretiva', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Solicitada': 'status-info',
      'Em Diagnóstico': 'status-secondary',
      'Em Andamento': 'status-warning',
      'Aguardando Peças': 'status-warning',
      'Concluída': 'status-success',
      'Cancelada': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getStatusText = (status: string): string => {
    return status || 'Desconhecido';
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'Crítica': 'status-danger',
      'Urgente': 'status-danger',
      'Alta': 'status-warning',
      'Média': 'status-info',
      'Baixa': 'status-success'
    };
    return priorityMap[prioridade] || 'status-secondary';
  };

  const getPriorityText = (prioridade: string): string => {
    return prioridade || 'Desconhecida';
  };

  const getDowntimeClass = (tempo: number): string => {
    if (tempo > 8) return 'status-danger';
    if (tempo > 4) return 'status-warning';
    return 'status-success';
  };

  const getDowntimeText = (tempo: number): string => {
    if (tempo > 8) return 'Alto';
    if (tempo > 4) return 'Médio';
    return 'Baixo';
  };

  const calculateEconomy = (estimado: number, real: number): number => {
    return estimado - real;
  };

  const calculateEconomyPercentage = (estimado: number, real: number): number => {
    return estimado > 0 ? ((estimado - real) / estimado) * 100 : 0;
  };

  const calculateDowntimeCost = (tempo: number, custoHora: number = 150): number => {
    return tempo * custoHora;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-corretivas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Manutenção...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes da manutenção corretiva...</p>
        </div>
      </div>
    );
  }

  if (error || !maintenance) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-corretivas')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Manutenção não encontrada'}</h3>
              <p>A manutenção corretiva solicitada não pôde ser carregada.</p>
              <button className="btn btn-primary" onClick={() => navigate('/manutencoes-corretivas')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const economy = calculateEconomy(maintenance.custo_estimado, maintenance.custo_real);
  const economyPercentage = calculateEconomyPercentage(maintenance.custo_estimado, maintenance.custo_real);
  const downtimeCost = calculateDowntimeCost(maintenance.tempo_total);
  const totalCost = Number(maintenance.custo_real || 0) + downtimeCost;

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/manutencoes-corretivas')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes da Manutenção Corretiva</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/manutencoes-corretivas/editar/${maintenance.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <h2>Manutenção Corretiva #{maintenance.id}</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                {getStatusText(maintenance.status)}
              </span>
              <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                {getPriorityText(maintenance.prioridade)}
              </span>
              <span className={`status-badge ${getDowntimeClass(maintenance.tempo_total)}`}>
                {formatNumber(maintenance.tempo_total)}h - {getDowntimeText(maintenance.tempo_total)}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Descrição:</label>
                <span className="info-value">{maintenance.descricao}</span>
              </div>
              <div className="info-item">
                <label>Veículo:</label>
                <span className="info-value">
                  <strong>{maintenance.veiculo}</strong>
                  {maintenance.veiculo_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {maintenance.veiculo_info.marca} {maintenance.veiculo_info.modelo} ({maintenance.veiculo_info.ano})
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Tipo de Problema:</label>
                <span className="info-value">
                  <span className="status-badge status-info">
                    {maintenance.tipo_problema}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Sintomas:</label>
                <span className="info-value">{maintenance.sintomas}</span>
              </div>
              <div className="info-item">
                <label>Causa Raiz:</label>
                <span className="info-value">{maintenance.causa_raiz}</span>
              </div>
              <div className="info-item">
                <label>Solução Aplicada:</label>
                <span className="info-value">{maintenance.solucao_aplicada}</span>
              </div>
              <div className="info-item">
                <label>Data de Solicitação:</label>
                <span className="info-value">{formatDateTime(maintenance.data_solicitacao)}</span>
              </div>
              <div className="info-item">
                <label>Data de Início:</label>
                <span className="info-value">
                  {maintenance.data_inicio ? formatDateTime(maintenance.data_inicio) : '-'}
                </span>
              </div>
              <div className="info-item">
                <label>Data de Conclusão:</label>
                <span className="info-value">
                  {maintenance.data_conclusao ? formatDateTime(maintenance.data_conclusao) : '-'}
                </span>
              </div>
              <div className="info-item">
                <label>Tempo Total:</label>
                <span className="info-value">
                  <span className={`status-badge ${getDowntimeClass(maintenance.tempo_total)}`}>
                    {formatNumber(maintenance.tempo_total)} horas
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Custo Estimado:</label>
                <span className="info-value">{formatCurrency(maintenance.custo_estimado)}</span>
              </div>
              <div className="info-item">
                <label>Custo Real:</label>
                <span className="info-value">
                  <strong>{formatCurrency(maintenance.custo_real)}</strong>
                </span>
              </div>
              <div className="info-item">
                <label>Fornecedor:</label>
                <span className="info-value">
                  {maintenance.fornecedor}
                  {maintenance.fornecedor_info && (
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      {maintenance.fornecedor_info.telefone}
                    </div>
                  )}
                </span>
              </div>
              <div className="info-item">
                <label>Status:</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                    {getStatusText(maintenance.status)}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Prioridade:</label>
                <span className="info-value">
                  <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                    {getPriorityText(maintenance.prioridade)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Análise de custos e tempo de inatividade */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Análise de Custos e Tempo de Inatividade</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Custo Estimado:</label>
                <span className="info-value">{formatCurrency(maintenance.custo_estimado)}</span>
              </div>
              <div className="info-item">
                <label>Custo Real:</label>
                <span className="info-value">
                  <strong>{formatCurrency(maintenance.custo_real)}</strong>
                </span>
              </div>
              <div className="info-item">
                <label>Variação:</label>
                <span className="info-value">
                  <strong style={{ color: economy > 0 ? '#04C64F' : '#e74c3c' }}>
                    {economy > 0 ? '+' : ''}{formatCurrency(economy)}
                  </strong>
                </span>
              </div>
              <div className="info-item">
                <label>Percentual de Variação:</label>
                <span className="info-value">
                  <strong style={{ color: economy > 0 ? '#04C64F' : '#e74c3c' }}>
                    {economyPercentage.toFixed(1)}%
                  </strong>
                </span>
              </div>
              <div className="info-item">
                <label>Tempo de Inatividade:</label>
                <span className="info-value">
                  <span className={`status-badge ${getDowntimeClass(maintenance.tempo_total)}`}>
                    {formatNumber(maintenance.tempo_total)} horas
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Custo de Inatividade:</label>
                <span className="info-value">
                  <strong style={{ color: '#e74c3c' }}>
                    {formatCurrency(downtimeCost)}
                  </strong>
                </span>
              </div>
              <div className="info-item">
                <label>Custo Total (Manutenção + Inatividade):</label>
                <span className="info-value">
                  <strong style={{ color: '#e74c3c', fontSize: '1.1rem' }}>
                    {formatCurrency(totalCost)}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informações do fornecedor */}
        {maintenance.fornecedor_info && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Informações do Fornecedor</h3>
            </div>
            <div className="card-body">
              <div className="info-grid">
                <div className="info-item">
                  <label>Nome:</label>
                  <span className="info-value">{maintenance.fornecedor_info.nome}</span>
                </div>
                <div className="info-item">
                  <label>CNPJ:</label>
                  <span className="info-value">{maintenance.fornecedor_info.cnpj}</span>
                </div>
                <div className="info-item">
                  <label>Telefone:</label>
                  <span className="info-value">{maintenance.fornecedor_info.telefone}</span>
                </div>
                <div className="info-item">
                  <label>Endereço:</label>
                  <span className="info-value">{maintenance.fornecedor_info.endereco}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Observações */}
        {maintenance.observacoes && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Observações e Recomendações</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0 }}>
                {maintenance.observacoes}
              </p>
            </div>
          </div>
        )}

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(maintenance.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(maintenance.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID da Manutenção:</label>
                <span className="info-value">{maintenance.id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/manutencoes-corretivas')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/manutencoes-corretivas/editar/${maintenance.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Manutenção
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorrectiveMaintenanceView;
